import React, { useEffect, useMemo, useState } from 'react';
import { translate } from '../../Services/translation';
import {
  query as queryOrders,
  setOrder,
  changeCurrentPage,
} from '../../Actions/Order.action';
import { useDispatch } from 'react-redux';
import OrderListItemMobile from './ui/OrderListItemMobile';
import OrderListItemDesktop from './ui/OrderListItemDesktop';
import { useTypedSelector } from '../../Hooks/useTypedSelector';
import { calculatePager, Pager } from '../../Services/Pagination.service';
import Pagination from '../Pagination';
import { Order } from '../../Types/Order';
import { useNavigate } from 'react-router-dom';
import SkeletonListItemDesktop from './ui/SkeletonListItemDesktop';
import SkeletonListItemMobile from './ui/SkeletonListItemMobile';
import clsx from 'clsx';
import { MyPagesOrder } from './Types/MyPages';
import EditorString from '../EditorString/EditorString';

const MyPagesOrderHistory = ({ description }: MyPagesOrder) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const orders = useTypedSelector((state) => state.myPage.orders);
  const { current: userInfo } = useTypedSelector(
    (state) => state.userInfo
  );
  const {
    list: orderList,
    totalCount,
    errors,
    currentPage,
    isLoading,
  } = orders;
  const [pager, setPager] = useState<Pager>({});
  const generalError = useMemo(
    () =>
      errors && errors['general']?.length ? errors['general'][0] : null,
    [errors]
  );

  useEffect(() => {
    dispatch(queryOrders(currentPage ?? 1, false));
  }, [dispatch, currentPage]);

  useEffect(() => {
    setPager(calculatePager(totalCount, currentPage ?? 1));
  }, [totalCount, currentPage]);

  const onOrderClick = (order: Order) => {
    dispatch(setOrder(order));
    navigate(order.orderId);
  };

  const hasOrders = useMemo(
    () => orderList && orderList.length > 0,
    [orderList]
  );

  return (
    <div className="col-span-3">
      <h1 className="mb-2">{translate('mypage.order.title')}</h1>
      {description && (
        <div className="mb-4 max-w-3xl text-base lg:mb-6">
          <EditorString value={description?.value} />
        </div>
      )}
      {generalError && <p className="error">{generalError}</p>}
      {!isLoading && !hasOrders && (
        <h6>{translate('mypage.order.no-orders')}</h6>
      )}
      {(isLoading || hasOrders) && (
        <ul className="flex flex-col gap-4 lg:hidden">
          <SkeletonListItemMobile items={2} loading={isLoading} />
          {!isLoading &&
            orderList?.length > 0 &&
            orderList.map((order) => (
              <OrderListItemMobile
                order={order}
                key={order.externalOrderID + 'mobile'}
                onClick={onOrderClick}
                isB2b={userInfo?.isBusinessCustomer}
              />
            ))}
        </ul>
      )}
      {(isLoading || hasOrders) && (
        <ul className="overflow hidden rounded border border-solid border-gray-200 lg:block">
          <div
            className={clsx(
              userInfo?.isBusinessCustomer ? 'grid-cols-6' : 'grid-cols-5',
              'grid gap-4 border-b p-2 py-4'
            )}
          >
            <p className="flex items-center text-base font-bold">
              {translate('orderlist.column.ordernumber')}
            </p>
            <p className="flex items-center text-base font-bold">
              {translate('orderlist.column.orderdate')}
            </p>
            <p className="flex items-center text-base font-bold">
              {translate('orderdetail.grandtotal')}
            </p>
            {userInfo?.isBusinessCustomer && (
              <p className="flex items-center text-base font-bold">
                {translate('orderlist.column.b2b-ordernumber')}
              </p>
            )}
            <p className="flex items-center text-base font-bold">
              {translate('orderdetail.orderstatus')}
            </p>
            <p className="flex items-center text-base font-bold">
              {translate('orderlist.column.tracking')}
            </p>
          </div>
          <SkeletonListItemDesktop items={5} loading={isLoading} />
          {!isLoading &&
            orderList?.length > 0 &&
            orderList.map((order) => (
              <OrderListItemDesktop
                order={order}
                key={order.externalOrderID + 'desktop'}
                onClick={onOrderClick}
                isB2b={userInfo?.isBusinessCustomer}
              />
            ))}
        </ul>
      )}
      <div className="mt-4">
        <Pagination
          model={pager}
          onChange={(index) =>
            index !== currentPage && dispatch(changeCurrentPage(index))
          }
        />
      </div>
    </div>
  );
};

export default MyPagesOrderHistory;
